import React, { useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Breadcrumbs from "../../../inc/Breadcrumbs";
import Permission from "../../../inc/Permission";
import { ContextPage } from "./pageContext";
import { setTitle as setTitleAction } from "../../../app/reducers/app/actions";

// Логика страницы: параметры URL, навигация по роутам раздела, заголовок,
// крошки и проверка прав. Страница только декларирует — вся механика здесь.
export default function ProviderPage({
    route,
    routes,
    type = 'index',
    withContent = false,
    withContentKeyRoute = 'id',
    children,
}) {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const title = useSelector((state) => state.app.title);
    // подписка нужна, чтобы checkPermission пересчитался при смене прав
    const userPermissions = useSelector((state) => state.app.userPermissions);

    const setTitle = useCallback((newTitle) => {
        dispatch(setTitleAction(newTitle));
        document.title = newTitle ? `${newTitle} — Cloud Phone` : 'Cloud Phone';
    }, [dispatch]);

    const goPageIndex = useCallback(() => {
        const url = (routes && routes.index) ? routes.index.url() : null;
        navigate(url ?? '/');
    }, [routes, navigate]);

    const goPageBack = useCallback(() => navigate(-1), [navigate]);

    const goPageShow = useCallback((idValue) => {
        const url = (routes && routes.show) ? routes.show.url({ id: idValue }) : null;
        if (url) navigate(url);
    }, [routes, navigate]);

    const goPageCreate = useCallback(() => {
        const url = (routes && routes.create) ? routes.create.url() : null;
        if (url) navigate(url);
    }, [routes, navigate]);

    // Без аргумента — право текущего роута; со слагом ('create'|'edit'|'destroy'|'show') —
    // смежное право того же префикса. Роут без permission — открыт.
    const checkPermission = useCallback((slug = null) => {
        if (!route || !route.permission) return true;
        if (slug === null) return Permission.Check(route.permission.name);
        return Permission.Check(`${route.permission.prefix}.${slug}`);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [route, userPermissions]);

    // Имя записи в крошках — текущий заголовок страницы (его ставит форма из данных)
    const breadcrumb = useMemo(
        () => (route ? Breadcrumbs.For(route.name, { id, name: title }) : []),
        [route, id, title],
    );

    const value = useMemo(() => ({
        id,
        route,
        routes,
        type,
        withContent,
        withContentKeyRoute,
        goPageIndex,
        goPageBack,
        goPageShow,
        goPageCreate,
        setTitle,
        breadcrumb,
        checkPermission,
    }), [
        id, route, routes, type, withContent, withContentKeyRoute,
        goPageIndex, goPageBack, goPageShow, goPageCreate, setTitle, breadcrumb, checkPermission,
    ]);

    return <ContextPage.Provider value={value}>{children}</ContextPage.Provider>;
}
